// Manual reconciliation dialog — records a human decision (paid/failed) for a
// payment the provider never answered about. The note is REQUIRED: it must say
// what was verified and where (kept on the immutable audit trail). Shared by
// the run Disbursements tab and the tenant-wide Reconciliation page.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Textarea } from '@/features/shared/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/features/shared/components/ui/dialog';
import { payrollApi } from '../api/payrollApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { formatMoney } from '../lib/format';
import type { Currency, DisbursementItem } from '../types';

/** The backend requires a substantive note (what was verified, and where). */
export const RESOLVE_NOTE_MIN = 2; // deliberately short — a brief note like "NA" is allowed

interface ResolveDisbursementDialogProps {
  /** The reconciliation_required item to resolve — dialog is open while non-null. */
  item: DisbursementItem | null;
  /** Fallback for items that don't carry their own currency (batch context). */
  currency?: Currency;
  onClose: () => void;
  /** Called with the updated item after a successful resolve. */
  onResolved: (item: DisbursementItem) => void;
}

export const ResolveDisbursementDialog: React.FC<ResolveDisbursementDialogProps> = ({
  item,
  currency,
  onClose,
  onResolved,
}) => {
  const { t } = useTranslation('payroll');
  const [resolution, setResolution] = useState<'paid' | 'failed'>('paid');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset per item so a previous decision never leaks into the next one.
  useEffect(() => {
    if (item) {
      setResolution('paid');
      setNote('');
    }
  }, [item]);

  const tooShort = note.trim().length < RESOLVE_NOTE_MIN;
  const itemCurrency = item?.currency ?? currency ?? 'TZS';

  async function submit() {
    if (!item || tooShort) return;
    setBusy(true);
    try {
      const updated = await payrollApi.resolveDisbursement(item.id, {
        resolution,
        note: note.trim(),
      });
      toast.success(
        resolution === 'paid'
          ? t('reconciliation.resolvedPaid', { defaultValue: 'Payment marked as paid' })
          : t('reconciliation.resolvedFailed', { defaultValue: 'Payment marked as failed' }),
      );
      onResolved(updated);
      onClose();
    } catch (e: any) {
      // 409 conflict (already resolved / wrong state) — surface the server message.
      toast.error(
        getApiErrorMessage(e, t('reconciliation.resolveError', { defaultValue: 'Could not resolve the payment' })),
        { duration: 6000 },
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('reconciliation.resolveTitle', { defaultValue: 'Resolve payment' })}
          </DialogTitle>
          <DialogDescription>
            {item &&
              t('reconciliation.resolveDescription', {
                defaultValue:
                  '{{amount}} to {{name}} ({{destination}}) got no answer from the provider. Check the provider portal or statement, then record what actually happened.',
                amount: formatMoney(item.amount, itemCurrency),
                name: item.employeeName,
                destination: item.accountMasked,
              })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            {t('reconciliation.outcome', { defaultValue: 'Verified outcome' })}
          </p>
          <label className="flex items-start gap-2.5 rounded-lg border border-border p-3 text-sm cursor-pointer">
            <input
              type="radio"
              name="resolution"
              className="mt-0.5"
              checked={resolution === 'paid'}
              onChange={() => setResolution('paid')}
            />
            <span>
              <span className="font-medium text-foreground">
                {t('reconciliation.outcomePaid', { defaultValue: 'Paid' })}
              </span>
              <span className="block text-xs text-muted-foreground">
                {t('reconciliation.outcomePaidHint', {
                  defaultValue: 'The money reached the beneficiary — settle this item.',
                })}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2.5 rounded-lg border border-border p-3 text-sm cursor-pointer">
            <input
              type="radio"
              name="resolution"
              className="mt-0.5"
              checked={resolution === 'failed'}
              onChange={() => setResolution('failed')}
            />
            <span>
              <span className="font-medium text-foreground">
                {t('reconciliation.outcomeFailed', { defaultValue: 'Failed' })}
              </span>
              <span className="block text-xs text-muted-foreground">
                {t('reconciliation.outcomeFailedHint', {
                  defaultValue: 'The payment did not go through — mark it failed so it can be re-sent.',
                })}
              </span>
            </span>
          </label>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            {t('reconciliation.noteLabel', { defaultValue: 'Verification note (required)' })}
          </p>
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('reconciliation.noteHelper', {
              defaultValue:
                "What did you verify, and where? e.g. 'Confirmed paid on the AzamPay portal, txn ...'",
            })}
          />
          {tooShort && note.length > 0 && (
            <p className="text-xs text-[#991B1B]">
              {t('reconciliation.noteTooShort', {
                defaultValue: 'The note must be at least {{min}} characters.',
                min: RESOLVE_NOTE_MIN,
              })}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('reconciliation.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button disabled={busy || tooShort} onClick={submit}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('reconciliation.resolveConfirm', { defaultValue: 'Record resolution' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
